import { calculateChartSpacingAsPercentages, getAllDays } from '@front-app/hooks/DataDashboard/util';
import { useTranslate } from '@front-app/hooks/useTranslate';
import {
	reactive,
	SetupContext,
} from '@vue/composition-api';
import {
	BarSeriesOption,
	EChartsOption,
	LegendComponentOption,
	LineSeriesOption,
	SeriesOption,
	TooltipComponentOption,
	XAXisComponentOption,
	YAXisComponentOption,
} from 'echarts';

type TimeseriesPoint = [number, number];

interface EchartFormatterParams<T> {
	componentType: string;
	// Series type
	seriesType: string;
	// Series index in option.series
	seriesIndex: number;
	// Series name
	seriesName: string;
	// Data name, or category name
	name: string;
	// Data index in input data array
	dataIndex: number;
	// Original data as input
	data: T;
	// Value of data. In most series it is the same as data.
	// But in some series it is some part of the data (e.g., in map, radar)
	value: number | Array<unknown> | T;
	// encoding info of coordinate system
	// Key: coord, like ('x' 'y' 'radius' 'angle')
	// value: Must be an array, not null/undefined. Contain dimension indices, like:
	// {
	//     x: [2] // values on dimension index 2 are mapped to x axis.
	//     y: [0] // values on dimension index 0 are mapped to y axis.
	// }
	// dimension names list
	dimensionNames: Array<string>;
	// data dimension index, for example 0 or 1 or 2 ...
	// Only work in `radar` series.
	dimensionIndex: number;
	// Color of data
	color: string;
	// the percentage of pie chart
	percent: number;
}

export type SetSeriesFn = (series: SeriesOption[], minX: Date, maxX: Date, maxY: number) => void;

export interface EChartsStore {
	lineOption: EChartsOption;
	setSeries: SetSeriesFn;
}

const CHART_BUFFER = 30;
const EDGE_BUFFER = 18;
const ALL_TIME_LINE_CHART_HEIGHT = 6;
const WINDOW_LINE_CHART_HEIGHT = 30;
const BAR_CHART_HEIGHT = 24;
// For some reason titles all need to be adjusted upwards to look right
const TITLE_TOP_OFFSET = -11;

const toPercent = (val: number) => `${val}%`;

const CHART_SIZES = calculateChartSpacingAsPercentages(
	[
		ALL_TIME_LINE_CHART_HEIGHT,
		WINDOW_LINE_CHART_HEIGHT,
		BAR_CHART_HEIGHT,
	],
	EDGE_BUFFER,
	EDGE_BUFFER,
	CHART_BUFFER,
);

enum COLORS {
	ALL_TIME_TOTAL = '#6e6e6e',
	PERIOD_TOTAL = '#0060ae',
	GAIN_BAR = '#87ba40',
	LOSS_BAR = '#c33232',
	WINDOW_AVERAGE = '#05e2d2',
	WINDOW_AVERAGE_DISABLED = '#dddddd',
}

export const getAllTimeLineSeries = (name: string, data: TimeseriesPoint[] = []): LineSeriesOption => getLineSeries(name, data, 0, COLORS.ALL_TIME_TOTAL);
export const getGainBarSeries = (name: string, data: TimeseriesPoint[] = []): BarSeriesOption => getBarSeries(name, data, 1, COLORS.GAIN_BAR);
export const getLossBarSeries = (name: string, data: TimeseriesPoint[] = []): BarSeriesOption => getBarSeries(name, data, 1, COLORS.LOSS_BAR);
export const getWindowAverageSeries = (
	isDisabled: boolean,
	name: string,
	data: TimeseriesPoint[] = [],
): LineSeriesOption => getLineSeries(name, data, 1, isDisabled ? COLORS.WINDOW_AVERAGE : COLORS.WINDOW_AVERAGE_DISABLED);
export const getPeriodLineSeries = (name: string, data: TimeseriesPoint[] = []): LineSeriesOption => getLineSeries(name, data, 2, COLORS.PERIOD_TOTAL);

// NOTE: This is intended to match the "header-small" css class stylings
const TITLE_TEXT_STYLES = {
	fontFamily: 'Poppins, Open Sans, Verdana, sans-serif',
	fontWeight: 400,
	// NOTE: This must be 16px NOT 16rem
	// 16px is functionally equivalent to the 16rem value used in the .header-small css class
	// Why? We use pixels here because Safari renders the rems illegibly in this particular case
	fontSize: '16px',
	color: '#6e6e6e',
};

export const getEchartsStore = (ctx: SetupContext): EChartsStore => {
	const $t = useTranslate(ctx);
	const lineOption: EChartsOption = reactive({
		tooltip: getTooltip(ctx),
		axisPointer: {
			link: [{
				xAxisIndex: [0, 1, 2],
			}],
		},
		grid: [
			{
				top: toPercent(CHART_SIZES[0][0]),
				bottom: toPercent(CHART_SIZES[0][1]),
				left: 40,
				right: 40,
			},
			{
				top: toPercent(CHART_SIZES[1][0]),
				bottom: toPercent(CHART_SIZES[1][1]),
				left: 40,
				right: 40,
			},
			{
				top: toPercent(CHART_SIZES[2][0]),
				bottom: toPercent(CHART_SIZES[2][1]),
				left: 40,
				right: 40,
			},
		],
		title: [
			{
				top: toPercent(CHART_SIZES[0][0] + TITLE_TOP_OFFSET),
				padding: 0,
				text: $t('data-dashboard.graph-titles.all-time-totals') as unknown as string,
				textStyle: TITLE_TEXT_STYLES,
			},
			{
				top: toPercent(CHART_SIZES[1][0] + TITLE_TOP_OFFSET),
				padding: 0,
				text: $t('data-dashboard.graph-titles.daily-change') as unknown as string,
				textStyle: TITLE_TEXT_STYLES,
			},
			{
				top: toPercent(CHART_SIZES[2][0] + TITLE_TOP_OFFSET),
				padding: 0,
				text: $t('data-dashboard.graph-titles.cumulative-daily-change') as unknown as string,
				textStyle: TITLE_TEXT_STYLES,
			},
		],
		legend: [],
		xAxis: [
			getXAxis(0, new Date(0), new Date(0), [], ctx),
			getXAxis(1, new Date(0), new Date(0), [], ctx),
			getXAxis(2, new Date(0), new Date(0), [], ctx),
		],
		yAxis: [
			{
				type: 'value',
				gridIndex: 0,
				minInterval: 0,
			},
			{
				type: 'value',
				gridIndex: 1,
			},
			{
				type: 'value',
				gridIndex: 2,
			},
		],
		series: [],
	});

	const setSeries = (series: SeriesOption[], minX: Date, maxX: Date, maxY: number): void => {
		const allDays = getAllDays(minX, maxX);
		lineOption.legend = getLegend(series);
		lineOption.yAxis = getYAxes(maxY);
		lineOption.xAxis = [
			getXAxis(0, minX, maxX, allDays, ctx),
			getXAxis(1, minX, maxX, allDays, ctx),
			getXAxis(2, minX, maxX, allDays, ctx),
		];
		lineOption.series = [...series, getEmptyBarSeries()];
	};

	function getYAxes(maxY: number): YAXisComponentOption[] {
		return [
			{
				type: 'value',
				gridIndex: 0,
				minInterval: Math.floor((maxY * 1.1) / 2) + 1,
			},
			{
				type: 'value',
				gridIndex: 1,
			},
			{
				type: 'value',
				gridIndex: 2,
			},
		];
	}

	function getLegend(series: SeriesOption[]): LegendComponentOption[] {
		return [
			{
				orient: 'horizontal',
				left: 0,
				top: toPercent(CHART_SIZES[0][0] - 7),
				data: [{
					name: series[0].name as string,
				}],
			},
			{
				orient: 'horizontal',
				left: 0,
				top: toPercent(CHART_SIZES[1][0] - 7),
				data: series.slice(2, series.length).map(({ name }) => ({ name: name as string })),
			},
			{
				orient: 'horizontal',
				left: 0,
				top: toPercent(CHART_SIZES[2][0] - 7),
				data: [{
					name: series[1].name as string,
				}],
			},
		];
	}

	return {
		lineOption,
		setSeries,
	};
};

function getTooltip(ctx: SetupContext): TooltipComponentOption {
	const x: TooltipComponentOption = {
		trigger: 'axis',
		formatter: (params: unknown): string => {
			const p = params as EchartFormatterParams<TimeseriesPoint>[];
			const inner = p
				.map((param) => `<p>${param.seriesName}: ${ctx.root.$n(Math.abs(param.data[1]), 'short')}</p>`)
				.join('');
			return `<h2 class="header-small">${ctx.root.$d(new Date(p[0].data[0]), 'short')}</h2> ${inner}`;
		},
		axisPointer: {
			type: 'line',
			lineStyle: {
				width: 1,
			},
			shadowStyle: {
				color: {
					type: 'linear',
					x: 0,
					y: 0,
					x2: 100,
					y2: 100,
					global: false,
					colorStops: [{
						offset: 0,
						color: 'red', // color at 0% position
					}, {
						offset: 1,
						color: 'blue', // color at 100% position
					}],
				},
			},
		},
	};
	return x;
}

function getXAxis(gridIndex: number, min: Date, max: Date, data: Date[], ctx: SetupContext): XAXisComponentOption {
	return {
		gridIndex,
		min,
		max,
		data: data.map((d) => d.getTime()),
		type: 'time',
		scale: false,
		axisLabel: {
			// eslint-disable-next-line arrow-body-style
			formatter: (value: number, index: number): string => {
				return (index % 2 === 0) ? (ctx.root.$d(new Date(value), 'short') as string) : '';
			},
		},
		axisTick: {
			alignWithLabel: true,
		},
	};
}

const DATA_POINT_WIDTH = 1;

export function getLineSeries(name: string, data: TimeseriesPoint[] = [], axisIndex = 0, color?: string): LineSeriesOption {
	return {
		name,
		data,
		yAxisIndex: axisIndex,
		xAxisIndex: axisIndex,
		symbolSize: DATA_POINT_WIDTH,
		type: 'line',
		lineStyle: {
			color,
			width: 1,
		},
		itemStyle: {
			color,
		},
		emphasis: {
			itemStyle: {
				shadowBlur: 6,
				shadowOffsetX: 0,
				shadowColor: 'rgba(0, 0, 0, 0.4)',
			},
		},
	};
}

export function getBarSeries(name: string, data: TimeseriesPoint[] = [], axisIndex: number, color?: string): BarSeriesOption {
	return {
		name,
		data,
		stack: 'total',
		barWidth: DATA_POINT_WIDTH,
		barGap: 0,
		yAxisIndex: axisIndex,
		xAxisIndex: axisIndex,
		type: 'bar',
		itemStyle: {
			color,
		},
		emphasis: {
			itemStyle: {
				shadowBlur: 6,
				shadowOffsetX: 0,
				shadowColor: 'rgba(0, 0, 0, 0.5)',
			},
		},
	};
}

function getEmptyBarSeries(): BarSeriesOption {
	return {
		barWidth: DATA_POINT_WIDTH,
		barGap: 0,
		name: '',
		type: 'bar',
		data: [],
		stack: 'total',
	};
}
